/**
 * @file useMenuTargets.ts
 * @description Списки целей для пунктов меню (phase19.16 Р1; контракт §2 —
 * существующие списки: страницы, категории каталога, рубрики и теги блога,
 * правовые документы). Грузятся по требованию, когда в окне выбран вид
 * пункта; ошибка — текст «Не удалось загрузить список…» и ручной адрес.
 */

import { useCallback, useRef, useState } from "react";

import { catalogCategories } from "../../lib/api-catalog";
import { contentCategories, contentTags } from "../../lib/api-content";
import { loadLegalTargets } from "../../lib/api-legal-targets";
import { sitePages } from "../../lib/api-site-pages";
import { legalTargetDescription } from "../../lib/site-view/legal-targets";
import type { MenuTargetList } from "../../lib/site-view/menu-link-types";

/** Цель в списке выбора. */
export interface MenuTarget {
  id: string;
  label: string;
  /** Пояснение: «/about · опубликована». */
  description: string;
}

type Loader = () => Promise<MenuTarget[]>;

/** Реестр загрузчиков — по списку целей. */
const LOADERS: Readonly<Record<MenuTargetList, Loader>> = {
  pages: async () =>
    (await sitePages.list()).map((p) => ({
      id: p.id,
      label: p.title,
      description: `/${p.slug} · ${p.isPublished ? "опубликована" : "не опубликована"}`,
    })),
  categories: async () =>
    (await catalogCategories.list())
      .filter((c) => !c.isSystemRoot && !c.isExternal)
      .map((c) => ({ id: c.id, label: c.displayName, description: `/catalog/${c.slug}${c.isActive ? "" : " · скрыта"}` })),
  contentCategories: async () =>
    (await contentCategories.list()).map((c) => ({ id: c.id, label: c.name, description: `/blog/category/${c.slug}` })),
  contentTags: async () =>
    (await contentTags.list()).map((t) => ({ id: t.id, label: t.name, description: `/blog/tag/${t.slug}` })),
  // Поле выбора документа — `LegalTargetField`; список здесь — для подписей целей.
  legalDocuments: async () =>
    (await loadLegalTargets()).map((d) => ({ id: d.id, label: d.title, description: legalTargetDescription(d) })),
};

export interface MenuTargetsApi {
  lists: Partial<Record<MenuTargetList, MenuTarget[]>>;
  failed: Partial<Record<MenuTargetList, boolean>>;
  ensure: (list: MenuTargetList, force?: boolean) => void;
}

export function useMenuTargets(): MenuTargetsApi {
  const [lists, setLists] = useState<MenuTargetsApi["lists"]>({});
  const [failed, setFailed] = useState<MenuTargetsApi["failed"]>({});

  // Что уже запрошено: функция стабильна, повторный вызов не шлёт запрос.
  const requested = useRef(new Set<MenuTargetList>());

  const ensure = useCallback((list: MenuTargetList, force = false) => {
    if (!force && requested.current.has(list)) return;
    requested.current.add(list);
    setFailed((prev) => ({ ...prev, [list]: false }));
    LOADERS[list]()
      .then((items) => setLists((prev) => ({ ...prev, [list]: items })))
      .catch(() => setFailed((prev) => ({ ...prev, [list]: true })));
  }, []);

  return { lists, failed, ensure };
}
